import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface QuotationDisplaySetting {
  id: string
  organizationId: string
  name: string
  showHsnColumn: boolean
  showTaxSummary: boolean
  showSkuColumn: boolean
  showSerialNumbers: boolean
  showThumbnails: boolean
  showDescFullWidth: boolean
  hideSubtotalForGroups: boolean
  hidePlaceOfSupply: boolean
  displayUnit: 'short' | 'full'
  isDefault: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface QuotationDisplaySettingFilters {
  isActive?: boolean
  search?: string
}

export function useQuotationDisplaySettings(filters?: QuotationDisplaySettingFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['quotationDisplaySettings', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      if (filters?.search) params.set('search', filters.search)
      const res = await api.get<any>(`/master-data/display-settings?${params}`)
      return (res?.data || []) as QuotationDisplaySetting[]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useQuotationDisplaySettingMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['quotationDisplaySettings', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<QuotationDisplaySetting, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) =>
      api.post<any>('/master-data/display-settings', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<QuotationDisplaySetting> }) =>
      api.patch<any>(`/master-data/display-settings/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/display-settings/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/display-settings/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setDefault = useMutation({
    mutationFn: (id: string) =>
      api.post<any>(`/master-data/display-settings/${id}/set-default`, {}, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, remove, setDefault }
}